/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { cm_variables } from '@ajs/vendor/feedonomics/utilities';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    inject,
    input,
    Input,
    NgZone,
    OnDestroy,
    Output,
    Signal,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LogService } from '@app/core/services/log.service';
import { CodeMirrorModes } from '@app/modules/inputs/enums/code-mirror-modes.enum';
import { AggregatedDbFieldsType } from '@app/modules/inputs/types/aggregated-db-fields.type';
import { CodemirrorComponent } from '@ctrl/ngx-codemirror';
import CodeMirror, { Editor, ScrollInfo } from 'codemirror';
import { BehaviorSubject, filter, Subject, take, takeUntil, tap } from 'rxjs';

// This component seeks to be a seamless wrapper around ngx-codemirror and use all the same inputs and outputs,
// with the added functionality of autocompletion.
@Component({
    selector: 'fdx-code-input',
    styleUrl: './code-input.component.scss',
    templateUrl: './code-input.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // eslint-disable-next-line @angular-eslint/no-forward-ref
            useExisting: forwardRef(() => CodeInputComponent),
            multi: true
        }
    ],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CodeInputComponent implements ControlValueAccessor, OnDestroy {
    readonly fields: Signal<AggregatedDbFieldsType | undefined> = input<AggregatedDbFieldsType>();

    /* class applied to the created textarea */
    @Input() className: string = '';
    /* name applied to the created textarea */
    @Input() name: string = 'codemirror';
    /* autofocus setting applied to the created textarea */
    @Input() autoFocus: boolean = false;
    /**
     * set options for codemirror
     * @link http://codemirror.net/doc/manual.html#config
     */
    readonly options: Signal<{ [key: string]: any; }> = input.required<{ [key: string]: any; }>();
    /* preserve previous scroll position after updating value */
    @Input() preserveScrollPosition: boolean = false;
    /* creates a lighter instance where we may have multiple readonly queries.  This should not
     * be used for representing the disabled state */
    readonly textOnly: Signal<boolean> = input<boolean>(false);

    @HostBinding('class.double-min-height')
    @Input() doubleMinHeight?: boolean = false;

    /**
     * When the wrapping FormControl is disabled, controls how CodeMirror's
     * `readOnly` option is set:
     *   - 'nocursor' (default, legacy): editor is not focusable. No keyboard
     *     navigation into it; no text selection via keyboard.
     *   - 'readonly': editor is focusable and the cursor shows, but typing /
     *     paste are blocked. Lets users tab in and select text for copying.
     */
    @Input() disabledMode: 'nocursor' | 'readonly' = 'nocursor';

    /* called when the text cursor is moved */
    @Output() readonly cursorActivity: EventEmitter<Editor> = new EventEmitter<Editor>();
    /* called when the editor is focused or loses focus */
    @Output() readonly focusChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    /* called when the editor is scrolled */
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() readonly scroll: EventEmitter<ScrollInfo> = new EventEmitter<ScrollInfo>();
    /* called when file(s) are dropped */
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() readonly drop: EventEmitter<[Editor, DragEvent]> = new EventEmitter<[Editor, DragEvent]>();

    @ViewChild(CodemirrorComponent, { static: false })
    private readonly cmComponent: CodemirrorComponent;

    @ViewChild('runModeOutput', { static: false })
    private readonly runModeOutput: ElementRef<HTMLPreElement>;

    protected readonly config: Signal<{ [key: string]: any; }> = computed(() => {
        const baseConfig = this.options();
        if (this.textOnly()) {
            return {
                ...baseConfig,
                readOnly: true
            };
        }
        return baseConfig;
    });

    protected readonly themeClass: Signal<string> = computed(() => {
        const theme = this.options().theme;
        return theme ? `fdx-code-input-readonly cm-s-${theme}` : 'fdx-code-input-readonly cm-s-default';
    });

    afterViewInitCompleted$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly unsubscribe$: Subject<void> = new Subject<void>();

    get value(): string {
        return this.innerValue;
    }

    set value(val: string) {
        if (val !== this.innerValue) {
            this.innerValue = val;
            this.onChange(val);
        }
    }

    private innerValue: string = '';
    private readonly logService: LogService = inject(LogService);
    private readonly ngZone: NgZone = inject(NgZone);

    constructor() {
        // Handles view mode switching: creates/destroys the editor or runMode pre element
        effect(() => {
            const isTextOnly = this.textOnly();
            queueMicrotask(() => {
                if (isTextOnly) {
                    this.afterViewInitCompleted$.next(true);
                    this.renderWithRunMode();
                } else {
                    this.initializeEditor();
                }
            });
        });

        // Handles fields changes: defines modes and sets up autocomplete
        effect(() => {
            const fields = this.fields();
            if (!fields) {
                return;
            }
            const isTextOnly = this.textOnly();

            this.afterViewInitCompleted$
                .pipe(
                    filter((completed) => completed),
                    take(1),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe(() => {
                    if (isTextOnly) {
                        this.defineCodeMirrorMode(fields.validFields);
                        this.renderWithRunMode();
                    } else {
                        this.defineEditorMode(fields.validFields);
                        this.initCodeMirrorAutocomplete(fields.autocompleteFields);
                        this.applyCodeMirrorMode(fields.validFields);
                    }
                });
        });
    }

    private initializeEditor(): void {
        this.ngZone
            .runOutsideAngular(
                async () => {
                    await this.cmComponent.codeMirrorGlobal;
                    this.afterViewInitCompleted$.next(true);
                    if (this.innerValue) {
                        this.cmComponent.codeMirror.setValue(this.innerValue);
                    }
                }
            )
            .catch(
                (e) => {
                    this.logService.error(
                        '[CodeInputComponent] Failed to runOutsideAngular:',
                        e
                    );
                }
            );
    }

    ngOnDestroy(): void {
        this.afterViewInitCompleted$.complete();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private initCodeMirrorAutocomplete(autocompleteFields: string[]): void {
        if (autocompleteFields.length !== 0) {
            switch (this.options().mode) {
                case CodeMirrorModes.Transformer:
                    this.cmComponent.codeMirrorGlobal.registerHelper(
                        'hint',
                        CodeMirrorModes.Transformer,
                        cm_variables.hint_transformer_parser(autocompleteFields)
                    );
                    break;
                case CodeMirrorModes.GenAI:
                    this.cmComponent.codeMirrorGlobal.registerHelper(
                        'hint',
                        CodeMirrorModes.GenAI,
                        cm_variables.hint_genai(autocompleteFields)
                    );
                    break;
                default:
                    break;
            }
        }

        cm_variables.cmInit()(this.cmComponent.codeMirror);
    }

    private defineCodeMirrorMode(validFields: string[]): void {
        if (validFields.length === 0) {
            return;
        }
        switch (this.options().mode) {
            case CodeMirrorModes.Transformer:
                CodeMirror.defineSimpleMode(
                    CodeMirrorModes.Transformer,
                    cm_variables.mode_transformer_parser(validFields)
                );
                break;
            case CodeMirrorModes.GenAI:
                CodeMirror.defineSimpleMode(
                    CodeMirrorModes.GenAI,
                    cm_variables.mode_genai(validFields)
                );
                break;
            default:
                break;
        }
    }

    private defineEditorMode(validFields: string[]): void {
        if (validFields.length === 0) {
            return;
        }
        switch (this.options().mode) {
            case CodeMirrorModes.Transformer:
                this.cmComponent.codeMirrorGlobal.defineSimpleMode(
                    CodeMirrorModes.Transformer,
                    cm_variables.mode_transformer_parser(validFields)
                );
                break;
            case CodeMirrorModes.GenAI:
                this.cmComponent.codeMirrorGlobal.defineSimpleMode(
                    CodeMirrorModes.GenAI,
                    cm_variables.mode_genai(validFields)
                );
                break;
            default:
                break;
        }
    }

    private applyCodeMirrorMode(validFields: string[]): void {
        if (validFields.length === 0) {
            return;
        }
        switch (this.options().mode) {
            case CodeMirrorModes.Transformer:
                this.cmComponent.codeMirror.setOption('mode', CodeMirrorModes.Transformer);
                break;
            case CodeMirrorModes.GenAI:
                this.cmComponent.codeMirror.setOption('mode', CodeMirrorModes.GenAI);
                break;
            default:
                break;
        }
    }

    onFocusChange(focusing: boolean): void {
        if (!focusing) {
            this.onTouched();
        }
        this.focusChange.emit(focusing);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onChange(_: any): void { }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onTouched(): void { }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        // If the component started disabled, that state wouldn't reflect since the code editor
        // didn't exist.
        // CodeMirror's `readOnly` option accepts a boolean or the literal
        // string 'nocursor'. Map our public string-literal API to that shape.
        let readOnlyValue: boolean | 'nocursor' = false;
        if (isDisabled) {
            readOnlyValue = this.disabledMode === 'nocursor' ? 'nocursor' : true;
        }
        this.afterViewInitCompleted$.pipe(
            filter((completed) => completed),
            take(1),
            tap(() => this.cmComponent?.setOptionIfChanged('readOnly', readOnlyValue)),
            takeUntil(this.unsubscribe$)
        ).subscribe();
    }

    writeValue(value: string): void {
        if (value !== this.innerValue) {
            this.innerValue = value;
            if (this.textOnly()) {
                this.renderWithRunMode();
            } else {
                this.cmComponent?.codeMirror?.setValue(this.value);
            }
        }
    }

    private renderWithRunMode(): void {
        const el = this.runModeOutput?.nativeElement;
        if (!el) {
            return;
        }
        el.textContent = '';
        const mode = this.options().mode || 'text/plain';
        CodeMirror.runMode(this.innerValue || '', mode, el);
    }

    visibilityChange(isVisible: boolean): void {
        if (isVisible) {
            this.cmComponent?.codeMirror?.refresh();
        }
    }
}
